import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

import { FaGlobe, FaLanguage } from 'react-icons/fa';

const Languages = ({
  isScrolling,
  isHomepage,
  classList,
  align = 'right',
  mobileUpward = false,
}) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  const languages = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
    // { code: 'de', label: 'Deutsch' },
  ];

  const dropdownAlignment = align === 'left' ? 'left-0' : 'right-0';

  const toggleDropdown = () => setIsOpen(!isOpen);

  const selectLanguage = (language) => {
    if (['de'].includes(language.code)) {
      setShowModal(true);
      setIsOpen(false);
      return;
    }

    i18n.changeLanguage(language.code);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const dropdownTextColor =
    isScrolling || !isHomepage ? 'text-primary-blue' : 'text-gray800';

  return (
    <div className='relative' ref={dropdownRef}>
      <button
        className={`py-2 rounded flex items-center ${classList}`}
        onClick={toggleDropdown}
      >
        <FaGlobe size={20} className='mr-2' />
        <span className='text-base font-semibold'>
          {i18n.language.toUpperCase()}
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key='dropdown'
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className={`absolute ${mobileUpward ? 'bottom-full mb-2' : 'top-12'}
            w-48 rounded-md bg-white ring-1 ring-gray-300 shadow-md
            overflow-hidden z-40
            ${dropdownAlignment}
  `}
          >
            <ul>
              {languages.map((language) => {
                const isActive = i18n.language === language.code;
                return (
                  <li
                    key={language.code}
                    className={`px-4 py-2 hover:bg-gray-200 cursor-pointer transition-colors duration-150 ${
                      isActive ? 'text-primary-red' : dropdownTextColor
                    }`}
                    onClick={() => selectLanguage(language)}
                  >
                    <span className='text-lg'>{language.flag}</span> &nbsp;
                    {language.label}
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
      {showModal && (
        <div className='fixed z-50 inset-0 flex items-center justify-center bg-gray-900 bg-opacity-75'>
          <div className='relative bg-white p-6 md:p-12 rounded w-[80vw] lg:w-[60vw] h-[80vh] md:h-auto max-w-[80vw] max-h-[100vh] min-w-[300px] min-h-[300px] overflow-y-auto'>
            <button
              className='absolute text-primary-blue top-4 right-4 text-3xl'
              onClick={() => setShowModal(false)}
            >
              &times;
            </button>
            <div className='flex flex-col items-center text-center gap-6'>
              <h2 className='text-xl lg:text-2xl font-medium text-primary-red'>
                Coming Soon
              </h2>
              <FaLanguage size={60} style={{ color: '#363D5A' }} />
              <p className='text-justify text-base lg:text-lg font-light leading-8 text-primary-blue'>
                This language will be available soon.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Languages;
